'use client'

import Link from 'next/link'
import { AnimatedNumber } from '@/components/ui/animated-number'
import { Button } from '@/components/ui/button'
import {
  ArrowUpRight,
  // ── capability + module icon imports (lucide-style content names on the
  //    left of each registry entry, Carbon equivalents here) ──
  Activity,
  Warning,
  WarningAlt,
  Archive,
  ArrowUp,
  ArrowsVertical,
  ArrowDownRight,
  Badge,
  Information,
  Notification,
  Book,
  Box,
  Portfolio,
  Enterprise,
  EventSchedule,
  Calendar,
  CalendarAdd,
  CalendarHeatMap,
  ChartColumn,
  ChartBar,
  ChartMultitype,
  CheckmarkFilled,
  CheckboxChecked,
  Time,
  Money,
  Purchase,
  DataBase,
  CurrencyDollar,
  View,
  DocumentDownload,
  DocumentSigned,
  Document,
  Chemistry,
  FolderOpen,
  FolderMoveTo,
  Earth,
  Favorite,
  HealthCross,
  Help,
  RecentlyViewed,
  Workspace,
  Layers,
  Dashboard,
  Grid,
  Template,
  ChartLine,
  Link as LinkIcon,
  Map,
  Location,
  Trophy,
  Chat,
  Music,
  Gift,
  Pen,
  PenFountain,
  Percentage,
  Phone,
  ChartPie,
  Printer,
  QrCode,
  Receipt,
  Rocket,
  Report,
  Search,
  Security,
  SecurityServices,
  ShoppingBag,
  SettingsAdjust,
  Mobile,
  AiGenerate,
  Star,
  Pills,
  Ticket,
  Timer,
  Growth,
  DeliveryTruck,
  UserCertification,
  UserSettings,
  UserFollow,
  Group,
  Video,
  Tools,
  Lightning,
  // module-level icons (hub names)
  Education,
  Wallet,
  CertificateCheck,
  Screen,
  Package,
  Lifesaver,
  ContainerSoftware,
  Devices,
  Bus,
  Network_3,
} from '@carbon/icons-react'

import { Breadcrumbs } from '@/components/breadcrumbs'
import { ChromaText } from '@/components/ui/chroma-text'
import { MeshPanel } from '@/components/fx/mesh-panel'
import { PanelDots } from '@/components/fx/panel-dots'

/**
 * One template for every /schoolerp/features/[slug] page, rebuilt in the
 * site's language: a mesh-gradient hero card seeded per module, the
 * eyebrow / chroma-heading / muted-intro header grammar, borderless muted
 * cards at 8px corners, and bare accent icons. All copy — hero, problem
 * statement, capabilities, roles, steps, CTAs — comes through unchanged
 * from the content file and the previous template's inline strings.
 */

// ─── Types (page.tsx imports FeatureItem from here) ──────────────────────────
export interface FeatureItem {
  slug: string
  category: string
  categorySlug?: string
  iconName: string
  heroImage?: string
  title: string
  tagline: string
  heroStat?: { value: string; label: string }[]
  problemStatement: string
  capabilities: { icon: string; title: string; desc: string }[]
  whoBenefits: { role: string; benefit: string }[]
  howItWorks: { step: string; title: string; desc: string }[]
  mockupType?: string
  relatedSlugs?: string[]
}

// ─── Icon registry ────────────────────────────────────────────────────────────
// Content stores lucide-style names; this maps every name in use to its
// Carbon equivalent. Unknown or empty names fall back to a filled check.
const ICONS: Record<string, React.ComponentType<any>> = {
  Activity,
  AlertCircle: Warning,
  AlertTriangle: WarningAlt,
  Archive,
  ArrowUpCircle: ArrowUp,
  ArrowUpDown: ArrowsVertical,
  Award: Badge,
  BadgeInfo: Information,
  Bell: Notification,
  BookOpen: Book,
  Boxes: Box,
  Briefcase: Portfolio,
  Building2: Enterprise,
  CalendarCheck: EventSchedule,
  CalendarCheck2: EventSchedule,
  CalendarClock: EventSchedule,
  CalendarDays: Calendar,
  CalendarPlus: CalendarAdd,
  CalendarRange: CalendarHeatMap,
  ChartColumn,
  ChartColumnBig: ChartColumn,
  ChartNoAxesColumn: ChartBar,
  ChartNoAxesCombined: ChartMultitype,
  CheckCheck: CheckmarkFilled,
  CheckSquare: CheckboxChecked,
  Clock: Time,
  Coins: Money,
  CreditCard: Purchase,
  Database: DataBase,
  DollarSign: CurrencyDollar,
  Eye: View,
  FileDown: DocumentDownload,
  FilePenLine: DocumentSigned,
  FileText: Document,
  FlaskConical: Chemistry,
  FolderOpen,
  FolderUp: FolderMoveTo,
  Globe: Earth,
  Heart: Favorite,
  HeartPulse: HealthCross,
  HelpCircle: Help,
  History: RecentlyViewed,
  Kanban: Workspace,
  Layers,
  LayoutDashboard: Dashboard,
  LayoutGrid: Grid,
  LayoutTemplate: Template,
  LineChart: ChartLine,
  Link2: LinkIcon,
  Map,
  MapPin: Location,
  Medal: Trophy,
  MessageCircle: Chat,
  Music,
  PartyPopper: Gift,
  PenLine: Pen,
  PenTool: PenFountain,
  PercentCircle: Percentage,
  Phone,
  PhoneCall: Phone,
  PieChart: ChartPie,
  Printer,
  QrCode,
  Receipt,
  Rocket,
  ScrollText: Report,
  Search,
  ShieldAlert: Security,
  ShieldCheck: SecurityServices,
  Shirt: ShoppingBag,
  Sliders: SettingsAdjust,
  Smartphone: Mobile,
  Sparkles: AiGenerate,
  Star,
  Stethoscope: HealthCross,
  Syringe: Pills,
  Ticket,
  Timer,
  TrendingDown: ArrowDownRight,
  TrendingUp: Growth,
  Trophy,
  Truck: DeliveryTruck,
  UserCheck: UserCertification,
  UserCog: UserSettings,
  UserPlus: UserFollow,
  Users: Group,
  UsersRound: Group,
  Video,
  Wrench: Tools,
  Zap: Lightning,
  // module-level names used by related cards
  GraduationCap: Education,
  Wallet,
  ClipboardList: Report,
  BadgeCheck: CertificateCheck,
  Banknote: Money,
  HeartHandshake: Group,
  Monitor: Screen,
  Package,
  MessageCircleHeart: Chat,
  LifeBuoy: Lifesaver,
  Container: ContainerSoftware,
  MonitorSmartphone: Devices,
  Bus,
  Network: Network_3,
}

function Icon({
  name,
  size = 22,
  className,
}: {
  name?: string
  size?: number
  className?: string
}) {
  const C = (name && ICONS[name]) || CheckmarkFilled
  return <C size={size} className={className} aria-hidden />
}

// ─── Shared section header ────────────────────────────────────────────────────
function SectionHeader({
  eyebrow,
  title,
  sub,
}: {
  eyebrow: string
  title: string
  sub?: string
}) {
  return (
    <div className="mx-auto mb-10 max-w-3xl text-center sm:mb-12">
      <span className="text-[11px] font-black uppercase tracking-[0.24em] text-foreground">
        {eyebrow}
      </span>
      <h2 className="font-heading mt-3 text-2xl font-normal leading-tight tracking-tight text-foreground sm:text-3xl md:text-4xl">
        <ChromaText>{title}</ChromaText>
      </h2>
      {sub && (
        <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">
          {sub}
        </p>
      )}
    </div>
  )
}

/* The four modules that are also sold as products in their own right. Only
   these four: the mapping has to be an actual product page, so the other
   nineteen modules render no such line rather than a near-enough guess. */
const STANDALONE_PRODUCT: Record<
  string,
  { href: string; label: string } | undefined
> = {
  hrms: { href: '/products/hrms', label: 'HRMS software' },
  payroll: { href: '/products/payroll', label: 'payroll software' },
  lms: {
    href: '/products/lms',
    label: 'a learning management system',
  },
  'accounts-finance': {
    href: '/products/accounts',
    label: 'accounting software',
  },
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export function FeaturePageClient({
  feature,
  related,
}: {
  feature: FeatureItem | null
  related: FeatureItem[]
}) {
  if (!feature) return null

  const standalone = STANDALONE_PRODUCT[feature.slug]

  return (
    <main className="bg-background">
      {/* ── Hero — mesh gradient card seeded per module ── */}
      <section className="container mx-auto pt-10 sm:pt-14">
        <div className="relative overflow-hidden rounded-xl p-7 sm:p-9 lg:p-12">
          <MeshPanel seedKey={`feature-${feature.slug}`} />
          <PanelDots className="text-zinc-800 dark:text-white" />
          <div className="relative max-w-3xl text-foreground">
            {/* Replaces the old two-item kicker (School ERP / category), which
                skipped the module hub it sits under and emitted no schema. */}
            <Breadcrumbs
              className="text-foreground/70"
              items={[
                { name: 'Home', href: '/' },
                { name: 'School ERP', href: '/schoolerp' },
                { name: 'Features', href: '/schoolerp/features' },
                {
                  name: feature.title,
                  href: `/schoolerp/features/${feature.slug}`,
                },
              ]}
            />

            <h1 className="font-heading mt-6 text-3xl font-normal leading-tight tracking-tight sm:text-4xl md:text-5xl">
              {feature.title}
            </h1>

            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground/80 sm:text-base md:text-[17px]">
              {feature.tagline}
            </p>

            {feature.heroStat && feature.heroStat.length > 0 && (
              <div className="mt-8 flex flex-wrap gap-x-10 gap-y-5">
                {feature.heroStat.map((s) => (
                  <div key={s.label}>
                    <p className="text-[26px] font-medium leading-none tracking-[-0.02em] sm:text-[30px]">
                      <AnimatedNumber value={s.value} />
                    </p>
                    <p className="mt-1.5 text-[12px] text-foreground/70 sm:text-[13px]">
                      {s.label}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-9 flex flex-wrap gap-3">
              <Button asChild size="xl">
                <Link href="/schedule-meeting">Book a free demo</Link>
              </Button>
              <Button asChild variant="outline" size="xl">
                <Link href="#capabilities">Explore more</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ── The problem ── */}
      <section className="container mx-auto pt-16 sm:pt-20">
        <div className="grid gap-6 lg:grid-cols-[280px_1fr] lg:gap-14">
          <div>
            <span className="text-[11px] font-black uppercase tracking-[0.24em] text-emerald-600 dark:text-primary-active">
              The problem
            </span>
            <h2 className="font-heading mt-3 text-xl font-normal leading-snug tracking-tight text-foreground sm:text-2xl">
              Why this matters for your school
            </h2>
          </div>
          <blockquote className="rounded-xl border border-border/70 bg-background p-6 text-base italic leading-[1.8] text-muted-foreground sm:p-8 sm:text-lg">
            {feature.problemStatement}
          </blockquote>
        </div>
      </section>

      {/* ── Capabilities ── */}
      <section
        id="capabilities"
        className="container mx-auto scroll-mt-20 pt-16 sm:pt-24"
      >
        <SectionHeader
          eyebrow="What it does"
          title="Every capability you need, built in."
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
          {feature.capabilities.map((cap) => (
            <div
              key={cap.title}
              className="rounded-xl border border-border/70 bg-background p-5 sm:p-6"
            >
              <Icon
                name={cap.icon}
                className="text-emerald-600 dark:text-primary-active"
              />
              <h3 className="mt-3.5 text-[15px] font-semibold leading-snug text-foreground">
                {cap.title}
              </h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground sm:text-sm">
                {cap.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Who benefits ── */}
      <section className="container mx-auto pt-16 sm:pt-24">
        <SectionHeader
          eyebrow="Who it helps"
          title="Designed for every role in your school."
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
          {feature.whoBenefits.map((b) => (
            <div
              key={b.role}
              className="rounded-xl border border-border/70 bg-background p-5 sm:p-6"
            >
              <p className="text-[11px] font-black uppercase tracking-[0.18em] text-emerald-600 dark:text-primary-active">
                {b.role}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-foreground/85 sm:text-[15px]">
                {b.benefit}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="container mx-auto pt-16 sm:pt-24">
        <SectionHeader
          eyebrow="Getting started"
          title="Achieve a seamless setup completed within just a few days."
          sub="A smooth, guided setup tailored to how your school actually operates."
        />
        <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-3">
          {feature.howItWorks.map((s) => (
            <div
              key={s.step}
              className="rounded-xl border border-border/70 bg-background p-6 sm:p-7"
            >
              <p className="font-mono text-2xl font-bold tracking-tight text-emerald-600 dark:text-primary-active">
                {s.step}
              </p>
              <h3 className="mt-3 text-[15px] font-semibold text-foreground sm:text-base">
                {s.title}
              </h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground sm:text-sm">
                {s.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Final CTA — second mesh card, its own roll ── */}
      <section className="container mx-auto pt-16 sm:pt-24">
        <div className="relative overflow-hidden rounded-xl p-8 text-center sm:p-12 lg:p-16">
          <MeshPanel seedKey={`feature-${feature.slug}-cta`} />
          <PanelDots className="text-zinc-800 dark:text-white" />
          <div className="relative mx-auto max-w-2xl text-foreground">
            <span className="text-[11px] font-black uppercase tracking-[0.24em] text-foreground/80">
              Ready to transform?
            </span>
            <h2 className="font-heading mt-4 text-3xl font-normal leading-tight tracking-tight sm:text-4xl">
              See {feature.title} in action
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-foreground/80 sm:text-base">
              Book a 30-minute demo we'll walk through exactly how this fits
              your school.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button asChild size="xl">
                <Link href="/schedule-meeting">Book your free demo</Link>
              </Button>
              <Button asChild variant="outline" size="xl">
                {/* The module index, not the landing page's anchor — the index
                    is the page that carries all 23 modules with their taglines. */}
                <Link href="/schoolerp/features">Browse all modules</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Related modules ── */}
      {related.length > 0 && (
        <section className="container mx-auto pb-16 pt-16 sm:pb-24 sm:pt-24">
          <SectionHeader
            eyebrow="Connected modules"
            title="Works best alongside these."
            sub="Each module below shares the same records as this one, so nothing has to be entered twice."
          />
          {/* Four across, matching the four related modules every entry in
              @content/schoolerp/features now declares. */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
            {related.map((r) => (
              <Link
                key={r.slug}
                href={`/schoolerp/features/${r.slug}`}
                className="group flex items-start gap-3.5 rounded-xl border border-border/70 bg-background p-4 sm:p-5"
              >
                <Icon
                  name={r.iconName}
                  className="mt-0.5 shrink-0 text-emerald-600 dark:text-primary-active"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground/80">
                        {r.category}
                      </p>
                      <h3 className="mt-1 text-sm font-semibold leading-snug text-foreground sm:text-[15px]">
                        {r.title}
                      </h3>
                    </div>
                    <ArrowUpRight
                      size={14}
                      className="mt-0.5 shrink-0 text-muted-foreground/70 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                      aria-hidden
                    />
                  </div>
                  <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground line-clamp-2 sm:text-[13px]">
                    {r.tagline}
                  </p>
                </div>
              </Link>
            ))}
          </div>

          {standalone && (
            <p className="mt-8 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
              Colleges and universities buy this capability on its own as{' '}
              <Link
                href={standalone.href}
                className="font-medium text-foreground underline decoration-emerald-600/40 underline-offset-4 transition-colors hover:decoration-emerald-600 dark:decoration-primary-active/40 dark:hover:decoration-primary-active"
              >
                {standalone.label}
              </Link>
              , built on the same platform.
            </p>
          )}
        </section>
      )}
    </main>
  )
}

export default FeaturePageClient
